import {
  useGlobalNotifier,
  useMessage,
  useSpreadsheetState,
} from "@vrf/fr-vrf-core-react";
import { FrVrfSpreadsheet } from "@vrf/fr-vrf-spreadsheet-react";
import {
  FrVrfAccordion,
  FrVrfButton,
  FrVrfButtonPanel,
  FrVrfCheckboxField,
  FrVrfDropdownField,
  FrVrfFooter,
  FrVrfFormPanel,
  FrVrfInputDaterangeField,
  FrVrfPanel,
  FrVrfRadioButtonField,
  FrVrfSingleLineInputField,
} from "@vrf/fr-vrf-web-components-react";
import React, { useEffect, useMemo } from "react";
import { useNavigate, useLocation, createPath } from "react-router-dom";
import {
  ComponentDeploymentRequest,
  ComponentDeploymentRequestListPageInitialState,
  INIT_DEPLOY_REQ_ROW_STATUS,
  SpreadSheetState,
  usePageState,
} from ".";
import { useAppState } from "../../app";
import { ApplicationMessageScreen } from "../../messages";
import { useGetEdhProtocolList } from "../../services/data-list";
import {
  CmpdplymntReqtDetail,
  UpdateComponentDeploymentRequestsStatusRequest,
  useGetDeploymentRequests,
  useUpdateComponentDeploymentRequestsStatus,
} from "../../services/deployment-requests";
import { removeMilliseconds } from "../../util/date-util";
import {
  DeploymentRequestProcessingDivisionType,
  DeploymentRequestScreenProcessingDivisionType,
  DeploymentRequestType,
  EnvTypeCd,
  InDefCommonFlagType,
  TcktStsTypeCpntPrdDplymnt,
  TcktStsTypeCpntStgDplymnt,
} from "../../util/general-code/enums/general-codes";
import {
  clearBackenddMessages,
  mappingErrorResponseToSpreadsheet,
} from "../../util/spreadsheet-data-utils";
import {
  ComponentMap,
  DeploymentRequestInfomationInputDialogContent,
  DeploymentRequestInfomationInputDialogInitialState,
  DeploymentRequestInfomationInputDialogState,
  useDeploymentRequestInfomationInputDialogState,
} from "../dialog/deployment-request-infomation-input-dialog";
import { usePageNavigation } from "../use-page-navigation";
import {
  OPEN_PRD_TICKET_STATUS_LIST,
  OPEN_STG_TICKET_STATUS_LIST,
  TEMPLATE_FILE_URL,
  getMergedEdhProtocolValue,
  splitEdhProtocolValue,
} from "./ComponentDeploymentRequestListConstant";
import { stringify } from "../../util/querystring-util";

export const ComponentDeploymentRequestListPageContent = () => {
  const [_appState, setAppState] = useAppState();
  const [pageState, setPageState] = usePageState();
  const { dataList, searchForm, fieldPath } = pageState;
  const [
    deploymentRequestInfomationInputDialogState,
    setDeploymentRequestInfomationInputDialogState,
  ] = useDeploymentRequestInfomationInputDialogState();
  const [getEdhProtocolListResponse, sendGetEdhProtocolListRequest] =
    useGetEdhProtocolList();
  const { showToast, showConfirmationDialog, errorMessages, resetUserError } =
    useGlobalNotifier();
  const { isModalPage, initUserError } = usePageNavigation();
  const successToastMessage = (messageId: string) => {
    showToast({
      design: "completion",
      message: messageForScreen(messageId),
    });
  };
  const { messageForScreen } = useMessage();
  const navigate = useNavigate();
  const transition = (path: string) => {
    const href = createPath({
      pathname: `${path}`,
    });
    navigate(href);
  };

  const [spreadsheetState, setSpreadsheetState, initSpreadsheetState] =
    useSpreadsheetState<SpreadSheetState>();
  const spreadsheetInitialData = useMemo<SpreadSheetState>(() => {
    return {
      deploymentRequestList: pageState.deploymentRequestList,
      dataList,
      removeMilliseconds,
    };
  }, [pageState.deploymentRequestList, dataList]);

  const search = useLocation().search;
  const queryParameter = new URLSearchParams(search);
  const params = {
    screenMode:
      queryParameter.get("screenMode") ??
      DeploymentRequestScreenProcessingDivisionType.NORMAL_MODE,
    dipByRegion: queryParameter.get("dipByRegion") ?? "",
    componentId: queryParameter.get("componentId") ?? "",
    inOutTypeCd: queryParameter.get("inOutTypeCd") ?? "",
    edhPrtclTypeCd: queryParameter.get("edhPrtclTypeCd") ?? "",
    startDate: queryParameter.get("startDate") ?? "",
    endDate: queryParameter.get("endDate") ?? "",
    environment:
      queryParameter.get("environment") === EnvTypeCd.PRD
        ? EnvTypeCd.PRD
        : EnvTypeCd.STG,
  };
  const pageMode = useMemo(() => {
    return params.screenMode;
  }, [params.screenMode]);

  const templateFileUrl = useMemo(() => {
    let templateUrl = TEMPLATE_FILE_URL.NORMAL;
    switch (pageMode) {
      case DeploymentRequestScreenProcessingDivisionType.NORMAL_MODE:
        templateUrl = TEMPLATE_FILE_URL.NORMAL;
        break;

      case DeploymentRequestScreenProcessingDivisionType.BEFORE_APPROVE_MODE:
        templateUrl = TEMPLATE_FILE_URL.BEFORE_APPROVE;
        break;

      case DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE:
        templateUrl = TEMPLATE_FILE_URL.REVIEW;
        break;

      case DeploymentRequestScreenProcessingDivisionType.WITHDRAW_MODE:
        templateUrl = TEMPLATE_FILE_URL.WITHDRAW;
        break;

      case DeploymentRequestScreenProcessingDivisionType.RETRY_MODE:
        templateUrl = TEMPLATE_FILE_URL.RETRY;
        break;

      case DeploymentRequestScreenProcessingDivisionType.REVERT_MODE:
        templateUrl = TEMPLATE_FILE_URL.REVERT;
        break;
    }

    return templateUrl;
  }, [pageMode]);

  const [getGetDeploymentRequestsResponse, sendGetDeploymentRequests] =
    useGetDeploymentRequests();
  const [
    updateComponentDeploymentRequestsStatusResponse,
    sendUpdateComponentDeploymentRequestsStatus,
  ] = useUpdateComponentDeploymentRequestsStatus();

  const onInitialDisplayed = () => {
    initUserError();
    setAppState((prevState) => ({
      ...prevState,
      title: `Component Deployment Request List`,
    }));
    sendGetDeploymentRequests({
      dplymntReqType: DeploymentRequestType.COMPONENT,
      dplymntMode: params.screenMode,
      dplymntStsList: displayDplyStsList,
      dipByRgn: params.dipByRegion,
      cmpId: params.componentId,
      inOutTypeCd: params.inOutTypeCd,
      edhPrtclTypeCd: params.edhPrtclTypeCd,
      schedDtimeStart: params.startDate,
      schedDtimeEnd: params.endDate,
      dplymntEnv: params.environment,
    });
    setPageState((prevState) => ({
      ...prevState,
      searchForm: {
        ...prevState.searchForm,
        dipByRegion: params.dipByRegion,
        componentId: params.componentId,
        protocol: getMergedEdhProtocolValue(
          params.inOutTypeCd,
          params.edhPrtclTypeCd,
        ),
        scheduledDeploymentDate: {
          startDate: params.startDate,
          endDate: params.endDate,
        },
        environment: params.environment,
      },
    }));
    sendGetEdhProtocolListRequest({});
  };

  const determineDisplayDplyStsList = () => {
    if (
      params.screenMode ===
      DeploymentRequestScreenProcessingDivisionType.NORMAL_MODE
    ) {
      if (searchForm.incClosed) return [];

      const targetTicketStatusList =
        params.environment === EnvTypeCd.STG
          ? OPEN_STG_TICKET_STATUS_LIST
          : OPEN_PRD_TICKET_STATUS_LIST;
      return targetTicketStatusList;
    }
    if (
      params.screenMode ===
        DeploymentRequestScreenProcessingDivisionType.BEFORE_APPROVE_MODE ||
      params.screenMode ===
        DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE
    ) {
      if (params.environment === EnvTypeCd.STG) {
        return [
          TcktStsTypeCpntStgDplymnt.DEPLOYMENT_APPROVAL_PENDING,
          TcktStsTypeCpntStgDplymnt.REVERT_DEPLOYMENT_APPROVAL_PENDING,
        ];
      } else if (params.environment === EnvTypeCd.PRD) {
        return [
          TcktStsTypeCpntPrdDplymnt.DEPLOYMENT_APPROVAL_PENDING,
          TcktStsTypeCpntPrdDplymnt.REVERT_DEPLOYMENT_APPROVAL_PENDING,
        ];
      }
    }
    if (
      params.screenMode ===
      DeploymentRequestScreenProcessingDivisionType.WITHDRAW_MODE
    ) {
      if (params.environment === EnvTypeCd.STG) {
        return [
          TcktStsTypeCpntStgDplymnt.DEPLOYMENT_PENDING,
          TcktStsTypeCpntStgDplymnt.WAITING_FOR_RETRY,
          TcktStsTypeCpntStgDplymnt.DEPLOYMENT_FAILED,
          TcktStsTypeCpntStgDplymnt.REVERT_DEPLOYMENT_PENDING,
          TcktStsTypeCpntStgDplymnt.REVERT_WAITING_FOR_RETRY,
          TcktStsTypeCpntStgDplymnt.REVERT_DEPLOYMENT_FAILED,
        ];
      } else if (params.environment === EnvTypeCd.PRD) {
        return [
          TcktStsTypeCpntPrdDplymnt.DEPLOYMENT_PENDING,
          TcktStsTypeCpntPrdDplymnt.WAITING_FOR_RETRY,
          TcktStsTypeCpntPrdDplymnt.DEPLOYMENT_FAILED,
          TcktStsTypeCpntPrdDplymnt.REVERT_DEPLOYMENT_PENDING,
          TcktStsTypeCpntPrdDplymnt.REVERT_WAITING_FOR_RETRY,
          TcktStsTypeCpntPrdDplymnt.REVERT_DEPLOYMENT_FAILED,
        ];
      }
    }
    if (
      params.screenMode ===
      DeploymentRequestScreenProcessingDivisionType.RETRY_MODE
    ) {
      if (params.environment === EnvTypeCd.STG) {
        return [
          TcktStsTypeCpntStgDplymnt.DEPLOYMENT_FAILED,
          TcktStsTypeCpntStgDplymnt.REVERT_DEPLOYMENT_FAILED,
        ];
      } else if (params.environment === EnvTypeCd.PRD) {
        return [
          TcktStsTypeCpntPrdDplymnt.DEPLOYMENT_FAILED,
          TcktStsTypeCpntPrdDplymnt.REVERT_DEPLOYMENT_FAILED,
        ];
      }
    }
    if (
      params.screenMode ===
      DeploymentRequestScreenProcessingDivisionType.REVERT_MODE
    ) {
      if (params.environment === EnvTypeCd.STG) {
        return [TcktStsTypeCpntStgDplymnt.DEPLOYED];
      } else if (params.environment === EnvTypeCd.PRD) {
        return [TcktStsTypeCpntPrdDplymnt.DEPLOYED];
      }
    }
    return [];
  };
  const displayDplyStsList = determineDisplayDplyStsList();

  const onGetDeploymentRequestListRequestResponded = () => {
    if (!getGetDeploymentRequestsResponse?.deploymentRequestList) return;

    const newDeploymentRequestList: ComponentDeploymentRequest[] =
      getGetDeploymentRequestsResponse.deploymentRequestList.map(
        (deploymentRequest, index) => ({
          ...deploymentRequest,
          ...INIT_DEPLOY_REQ_ROW_STATUS,
          index,
        }),
      );

    setPageState((prevState) => ({
      ...prevState,
      deploymentRequestList: newDeploymentRequestList,
    }));
  };

  const onGetEdhProtocolListResponse = () => {
    if (!getEdhProtocolListResponse) return;

    const edhProtocolList = getEdhProtocolListResponse.edhProtocolList.map(
      (e) => ({
        label: e.edhPrtclDsplyName,
        value: getMergedEdhProtocolValue(e.inOutTypeCd, e.edhPrtclTypeCd),
      }),
    );

    setPageState((prevState) => ({
      ...prevState,
      dataList: {
        ...prevState.dataList,
        edhProtocolList: edhProtocolList,
      },
    }));
  };

  const onUpdateComponentDeploymentRequestsStatusResponded = () => {
    if (updateComponentDeploymentRequestsStatusResponse) {
      if (Array.isArray(updateComponentDeploymentRequestsStatusResponse)) {
        const currentrecords: ComponentDeploymentRequest[] = spreadsheetState
          ? [...spreadsheetState.deploymentRequestList]
          : [];

        //clear error
        const currentrecordsWithoutBackendError = currentrecords
          .map((row) => {
            if (row.messageList) {
              return row;
            }
            return { ...row, messageList: [] };
          })
          .map((row) => clearBackenddMessages(row));
        const newSpreadsheetRecordData: ComponentDeploymentRequest[] =
          mappingErrorResponseToSpreadsheet(
            updateComponentDeploymentRequestsStatusResponse,
            currentrecordsWithoutBackendError,
            ComponentDeploymentRequestListPageInitialState.displayColumn,
          );

        if (!spreadsheetState) return;
        setSpreadsheetState({
          ...spreadsheetState,
          deploymentRequestList: newSpreadsheetRecordData,
        });
        return;
      }
      successToastMessage(ApplicationMessageScreen.APP_SCN_00044);
      sendGetDeploymentRequests({
        dplymntReqType: DeploymentRequestType.COMPONENT,
        dplymntMode: params.screenMode,
        dplymntStsList: displayDplyStsList,
        dipByRgn: params.dipByRegion,
        cmpId: params.componentId,
        inOutTypeCd: params.inOutTypeCd,
        edhPrtclTypeCd: params.edhPrtclTypeCd,
        schedDtimeStart: params.startDate,
        schedDtimeEnd: params.endDate,
        dplymntEnv: params.environment,
      });
    }
  };

  useEffect(onInitialDisplayed, [
    params.screenMode,
    params.dipByRegion,
    params.componentId,
    params.inOutTypeCd,
    params.edhPrtclTypeCd,
    params.startDate,
    params.endDate,
    params.environment,
  ]);
  useEffect(onGetDeploymentRequestListRequestResponded, [
    getGetDeploymentRequestsResponse,
  ]);
  useEffect(onGetEdhProtocolListResponse, [getEdhProtocolListResponse]);
  useEffect(onUpdateComponentDeploymentRequestsStatusResponded, [
    updateComponentDeploymentRequestsStatusResponse,
  ]);

  /**
   * SearchContent
   */
  const valueChangeHandle = (name: string) => (event: CustomEvent) => {
    const value = event.detail.value;
    setPageState((prevState) => ({
      ...prevState,
      searchForm: {
        ...prevState.searchForm,
        [name]: value,
      },
    }));
  };
  const daterangeChangeHandle = (name: string) => (event: CustomEvent) => {
    setPageState((prevState) => ({
      ...prevState,
      searchForm: {
        ...prevState.searchForm,
        [name]: {
          startDate: event.detail.startDate,
          endDate: event.detail.endDate,
        },
      },
    }));
  };

  const onClickSearch = () => {
    const searchDeploymentRequests = () => {
      resetUserError();
      const [inOutTypeCd, edhPrtclTypeCd] = splitEdhProtocolValue(
        searchForm.protocol,
      );
      sendGetDeploymentRequests({
        dplymntReqType: DeploymentRequestType.COMPONENT,
        dplymntMode: params.screenMode,
        dplymntStsList: displayDplyStsList,
        dipByRgn: searchForm.dipByRegion,
        cmpId: searchForm.componentId,
        inOutTypeCd,
        edhPrtclTypeCd,
        schedDtimeStart: searchForm.scheduledDeploymentDate.startDate,
        schedDtimeEnd: searchForm.scheduledDeploymentDate.endDate,
        dplymntEnv: searchForm.environment,
      });
    };

    if (pageState.isEdited) {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        onOkButtonClicked: () => {
          searchDeploymentRequests();
        },
      });
    } else {
      searchDeploymentRequests();
    }
  };

  /**
   * Component Deployment Request List
   */
  const onCellClicked = (event: CustomEvent) => {
    const eventName = event.detail.name;
    const component: CmpdplymntReqtDetail = event.detail.value;

    if (eventName === "cmpId") {
      const targetParams = {
        rootCpntId: component.cmpprjRootcmpId,
        cpntId: component.cmpId,
        dipByRgn: component.cmpprjDip,
        godFlg: InDefCommonFlagType.NO,
      };
      if (pageState.isEdited) {
        showConfirmationDialog({
          type: "confirm",
          title: "",
          message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
          onOkButtonClicked: () => {
            transition(`/component-detail?${stringify(targetParams)}`);
          },
        });
      } else {
        transition(`/component-detail?${stringify(targetParams)}`);
      }
    }
    if (eventName === "cpntId_newTabIcon") {
      const targetParams = {
        rootCpntId: component.cmpprjRootcmpId,
        cpntId: component.cmpId,
        dipByRgn: component.cmpprjDip,
        godFlg: InDefCommonFlagType.NO,
      };
      const href = createPath({
        pathname: `/component-detail?${stringify(targetParams)}`,
      });
      window.open(window.location.pathname + "#" + href);
    }
  };

  const onCellEdited = (event: CustomEvent) => {
    if (!spreadsheetState) return;
    const eventName = event.detail.name;

    const value = event.detail.cell.value;

    if (eventName === "isSelected") {
      const targetDeploymentRequest: ComponentDeploymentRequest =
        event.detail.value;

      const newDeploymentRequestList: ComponentDeploymentRequest[] =
        spreadsheetState.deploymentRequestList.map((e) =>
          e.dplymntReqSeq === targetDeploymentRequest.dplymntReqSeq
            ? {
                ...e,
                isSelected:
                  e.isSelected === InDefCommonFlagType.NO
                    ? InDefCommonFlagType.YES
                    : InDefCommonFlagType.NO,
              }
            : e,
        );

      setSpreadsheetState({
        ...spreadsheetState,
        deploymentRequestList: newDeploymentRequestList,
      });
      setPageState((prev) => ({ ...prev, isEdited: true }));
    }
    if (eventName === "action") {
      const targetDeploymentRequest: ComponentDeploymentRequest =
        event.detail.value;
      const newDeploymentRequestList: ComponentDeploymentRequest[] =
        spreadsheetState.deploymentRequestList.map((e) =>
          e.dplymntReqSeq === targetDeploymentRequest.dplymntReqSeq
            ? {
                ...e,
                isApproved: value,
              }
            : e,
        );
      setSpreadsheetState({
        ...spreadsheetState,
        deploymentRequestList: newDeploymentRequestList,
      });
      setPageState((prev) => ({ ...prev, isEdited: true }));
    }
    if (eventName === "review") {
      const targetDeploymentRequest: ComponentDeploymentRequest =
        event.detail.value;
      const newDeploymentRequestList: ComponentDeploymentRequest[] =
        spreadsheetState.deploymentRequestList.map((e) =>
          e.dplymntReqSeq === targetDeploymentRequest.dplymntReqSeq
            ? {
                ...e,
                rvwCmnt: value,
              }
            : e,
        );
      setSpreadsheetState({
        ...spreadsheetState,
        deploymentRequestList: newDeploymentRequestList,
      });
      setPageState((prev) => ({ ...prev, isEdited: true }));
    }
  };

  /**
   * Footer
   */
  const onChangeMode = (targetMode: string) => () => {
    const [inOutTypeCd, edhPrtclTypeCd] = splitEdhProtocolValue(
      searchForm.protocol,
    );
    const newParams = {
      screenMode: targetMode,
      dipByRegion: searchForm.dipByRegion,
      componentId: searchForm.componentId,
      inOutTypeCd,
      edhPrtclTypeCd,
      startDate: searchForm.scheduledDeploymentDate.startDate,
      endDate: searchForm.scheduledDeploymentDate.endDate,
      environment: searchForm.environment,
    };
    transition(`/component-deployment-request-list?${stringify(newParams)}`);
  };

  const onClickBackButton = () => {
    if (pageState.isEdited) {
      showConfirmationDialog({
        type: "confirm",
        title: "",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00033),
        onOkButtonClicked: () => {
          navigate(-1);
        },
      });
    } else {
      navigate(-1);
    }
  };

  const onClickDiscard = () => {
    if (!spreadsheetState) return;
    const dplymntReqSeqList = spreadsheetState.deploymentRequestList
      .filter((e) => e.isSelected === InDefCommonFlagType.YES)
      .map((e) => ({
        index: e.index,
        processTyp: DeploymentRequestProcessingDivisionType.DISCARD,
        dplymntReqSeq: e.dplymntReqSeq,
        updateDtimeTDplymntReqMng: e.updateDtimeTDplymntReqMng,
        tcktStsIdFrom: e.dplymntSts,
      }));
    if (dplymntReqSeqList.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: `Input Deployment Request Information `,
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00168),
      onOkButtonClicked: () => {
        resetUserError();
        const params: UpdateComponentDeploymentRequestsStatusRequest = {
          dplymntReqSeqList: dplymntReqSeqList,
        };
        sendUpdateComponentDeploymentRequestsStatus(params);
      },
    });
  };

  const onClickChangeDeploymentDate = () => {
    if (!spreadsheetState) return;

    const deploymentRequestList = spreadsheetState.deploymentRequestList
      .filter((e) => e.isSelected === InDefCommonFlagType.YES)
      .map((e) => {
        return {
          dplymntReqSeq: e.dplymntReqSeq,
          updateDtimeTDplymntReqMng: e.updateDtimeTDplymntReqMng,
        };
      });

    if (deploymentRequestList.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }

    const componentMap: ComponentMap[] =
      spreadsheetState.deploymentRequestList.map((e, index) => {
        return {
          index: index,
          pjtId: e.jediPjtId,
          tcktId: e.cmpdplymntReqtDetail[0].tckId,
        };
      });

    const dialogState: DeploymentRequestInfomationInputDialogState = {
      ...DeploymentRequestInfomationInputDialogInitialState,
      dplymntReqType: DeploymentRequestType.COMPONENT,
      processTyp: params.environment,
      deploymentRequestList: deploymentRequestList,
      componentMap,
      isShow: true,
    };
    setDeploymentRequestInfomationInputDialogState(dialogState);
  };

  const onClickCompleteReview = () => {
    if (!spreadsheetState) return;
    const dplymntReqSeqList = spreadsheetState.deploymentRequestList
      .filter((e) => e.isApproved.length > 0)
      .map((e) => ({
        index: e.index,
        processTyp:
          e.isApproved === InDefCommonFlagType.YES
            ? DeploymentRequestProcessingDivisionType.APPROVE
            : DeploymentRequestProcessingDivisionType.REJECT,
        dplymntReqSeq: e.dplymntReqSeq,
        updateDtimeTDplymntReqMng: e.updateDtimeTDplymntReqMng,
        tcktStsIdFrom: e.dplymntSts,
        reviewComment: e.rvwCmnt,
      }));
    if (dplymntReqSeqList.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: `Input Deployment Request Information `,
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00133),
      onOkButtonClicked: () => {
        resetUserError();
        const params: UpdateComponentDeploymentRequestsStatusRequest = {
          dplymntReqSeqList: dplymntReqSeqList,
        };
        sendUpdateComponentDeploymentRequestsStatus(params);
      },
    });
  };

  const onClickWithdrawRequest = () => {
    if (!spreadsheetState) return;
    const dplymntReqSeqList = spreadsheetState.deploymentRequestList
      .filter((e) => e.isSelected === InDefCommonFlagType.YES)
      .map((e) => ({
        index: e.index,
        processTyp: DeploymentRequestProcessingDivisionType.WITHDRAW,
        dplymntReqSeq: e.dplymntReqSeq,
        updateDtimeTDplymntReqMng: e.updateDtimeTDplymntReqMng,
        tcktStsIdFrom: e.dplymntSts,
      }));

    if (dplymntReqSeqList.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: `Input Deployment Request Information`,
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00134),
      onOkButtonClicked: () => {
        resetUserError();
        const params: UpdateComponentDeploymentRequestsStatusRequest = {
          dplymntReqSeqList: dplymntReqSeqList,
        };
        sendUpdateComponentDeploymentRequestsStatus(params);
      },
    });
  };

  const onClickRetryRequest = () => {
    if (!spreadsheetState) return;
    const dplymntReqSeqList = spreadsheetState.deploymentRequestList
      .filter((e) => e.isSelected === InDefCommonFlagType.YES)
      .map((e) => ({
        index: e.index,
        processTyp: DeploymentRequestProcessingDivisionType.RETRY,
        dplymntReqSeq: e.dplymntReqSeq,
        updateDtimeTDplymntReqMng: e.updateDtimeTDplymntReqMng,
        tcktStsIdFrom: e.dplymntSts,
      }));

    if (dplymntReqSeqList.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }

    showConfirmationDialog({
      type: "confirm",
      title: `Input Deployment Request Information`,
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00135),
      onOkButtonClicked: () => {
        resetUserError();
        const params: UpdateComponentDeploymentRequestsStatusRequest = {
          dplymntReqSeqList: dplymntReqSeqList,
        };
        sendUpdateComponentDeploymentRequestsStatus(params);
      },
    });
  };

  const onClickRevertRequest = () => {
    if (!spreadsheetState) return;
    const dplymntReqSeqList = spreadsheetState.deploymentRequestList
      .filter((e) => e.isSelected === InDefCommonFlagType.YES)
      .map((e) => ({
        index: e.index,
        processTyp: DeploymentRequestProcessingDivisionType.REVERT,
        dplymntReqSeq: e.dplymntReqSeq,
        updateDtimeTDplymntReqMng: e.updateDtimeTDplymntReqMng,
        tcktStsIdFrom: e.dplymntSts,
      }));
    if (dplymntReqSeqList.length === 0) {
      showToast({
        design: "alert",
        message: messageForScreen(ApplicationMessageScreen.APP_SCN_00035),
      });
      return;
    }
    showConfirmationDialog({
      type: "confirm",
      title: `Input Deployment Request Information`,
      message: messageForScreen(ApplicationMessageScreen.APP_SCN_00136),
      onOkButtonClicked: () => {
        resetUserError();
        const params: UpdateComponentDeploymentRequestsStatusRequest = {
          dplymntReqSeqList: dplymntReqSeqList,
        };
        sendUpdateComponentDeploymentRequestsStatus(params);
      },
    });
  };

  return (
    <>
      {/* Search Content */}
      <>
        <FrVrfAccordion>
          <FrVrfPanel>
            <FrVrfFormPanel columns={4}>
              <FrVrfSingleLineInputField
                labelContent="DIP By Region"
                content={searchForm.dipByRegion}
                onInputValueChanged={valueChangeHandle("dipByRegion")}
                errorMessage={errorMessages(
                  fieldPath.dipByRegion,
                  isModalPage(),
                )}
              />
              <FrVrfSingleLineInputField
                labelContent="Component ID"
                content={searchForm.componentId}
                onInputValueChanged={valueChangeHandle("componentId")}
                errorMessage={errorMessages(
                  fieldPath.componentId,
                  isModalPage(),
                )}
              />
              <FrVrfDropdownField
                autocomplete={false}
                labelContent="EDH Protocol"
                selectedValue={searchForm.protocol}
                items={dataList.edhProtocolList}
                onValueChanged={valueChangeHandle("protocol")}
                errorMessage={errorMessages(fieldPath.protocol, isModalPage())}
              />
              <FrVrfInputDaterangeField
                labelContent="Scheduled Deployment Date"
                inputDateRange={searchForm.scheduledDeploymentDate}
                onInputValueChanged={daterangeChangeHandle(
                  "scheduledDeploymentDate",
                )}
                clearButtonVisible
                errorMessage={errorMessages(
                  fieldPath.scheduledDeploymentDate,
                  isModalPage(),
                )}
              />

              <FrVrfCheckboxField
                labelContent="Include closed"
                onCheckedChanged={valueChangeHandle("incClosed")}
                checked={searchForm.incClosed}
                visible={
                  pageMode ==
                  DeploymentRequestScreenProcessingDivisionType.NORMAL_MODE
                }
              />

              <FrVrfRadioButtonField
                labelContent="STG/PRD"
                optionItems={[
                  { label: "STG", value: EnvTypeCd.STG },
                  { label: "PRD", value: EnvTypeCd.PRD },
                ]}
                checked={searchForm.environment}
                required
                onRadioCheckedChanged={valueChangeHandle("environment")}
                errorMessage={errorMessages(
                  fieldPath.environment,
                  isModalPage(),
                )}
              />
            </FrVrfFormPanel>
            <FrVrfButtonPanel>
              <FrVrfButton
                content="Search"
                slot="right"
                theme="primary"
                size="medium"
                onClick={onClickSearch}
              />
            </FrVrfButtonPanel>
          </FrVrfPanel>
        </FrVrfAccordion>
      </>
      <br />
      <FrVrfPanel>
        <FrVrfSpreadsheet
          data={spreadsheetInitialData}
          templateUrl={templateFileUrl}
          onInitialized={initSpreadsheetState}
          onCellClicked={onCellClicked}
          onCellEdited={onCellEdited}
          maxHeight={720}
        />
      </FrVrfPanel>
      {/* Footer */}
      <FrVrfFooter>
        {/* normal */}
        {pageMode ===
          DeploymentRequestScreenProcessingDivisionType.NORMAL_MODE && (
          <>
            <FrVrfButton
              slot="left"
              content="Change"
              theme="secondary"
              size="large"
              onClick={onChangeMode(
                DeploymentRequestScreenProcessingDivisionType.BEFORE_APPROVE_MODE,
              )}
              authActId="component_deployment_request_list_normal.button.change"
            />
            <FrVrfButton
              slot="left"
              content="Review"
              theme="secondary"
              size="large"
              onClick={onChangeMode(
                DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE,
              )}
              authActId="component_deployment_request_list_normal.button.review"
            />
            <FrVrfButton
              slot="left"
              content="Withdraw Request"
              theme="secondary"
              size="large"
              onClick={onChangeMode(
                DeploymentRequestScreenProcessingDivisionType.WITHDRAW_MODE,
              )}
              authActId="component_deployment_request_list_normal.button.withdraw_request"
            />
            <FrVrfButton
              slot="left"
              content="Retry"
              theme="secondary"
              size="large"
              onClick={onChangeMode(
                DeploymentRequestScreenProcessingDivisionType.RETRY_MODE,
              )}
              authActId="component_deployment_request_list_normal.button.retry"
            />
            <FrVrfButton
              slot="left"
              content="Revert"
              theme="secondary"
              size="large"
              onClick={onChangeMode(
                DeploymentRequestScreenProcessingDivisionType.REVERT_MODE,
              )}
              authActId="component_deployment_request_list_normal.button.revert"
            />
          </>
        )}

        {/* before_approve_change */}
        {pageMode ===
          DeploymentRequestScreenProcessingDivisionType.BEFORE_APPROVE_MODE && (
          <>
            <FrVrfButton
              content="Back Component Deployment Request List"
              theme="secondary"
              size="large"
              onClick={onClickBackButton}
            />
            <FrVrfButton
              content="Discard"
              theme="primary"
              size="large"
              authActId="component_deployment_request_list_before_approve_change.button.discard"
              onClick={onClickDiscard}
            />
            <FrVrfButton
              content="Change Deployment Date"
              theme="primary"
              size="large"
              authActId="component_deployment_request_list_before_approve_change.button.change_deployment_date"
              onClick={onClickChangeDeploymentDate}
            />
          </>
        )}

        {/* review */}
        {pageMode ===
          DeploymentRequestScreenProcessingDivisionType.REVIEW_MODE && (
          <>
            <FrVrfButton
              content="Back Component Deployment Request List"
              theme="secondary"
              size="large"
              onClick={onClickBackButton}
            />
            <FrVrfButton
              content="Complete Review"
              theme="primary"
              size="large"
              authActId="component_deployment_request_list_review.button.complete_review"
              onClick={onClickCompleteReview}
            />
          </>
        )}

        {/* withdraw_retry_discard */}
        {pageMode ===
          DeploymentRequestScreenProcessingDivisionType.WITHDRAW_MODE && (
          <>
            <FrVrfButton
              content="Back Component Deployment Request List"
              theme="secondary"
              size="large"
              onClick={onClickBackButton}
            />
            <FrVrfButton
              content="Discard"
              theme="primary"
              size="large"
              authActId="component_deployment_request_list_withdraw_retry_discard.button.discard"
              onClick={onClickDiscard}
            />
            <FrVrfButton
              content="Withdraw Request"
              theme="primary"
              size="large"
              authActId="component_deployment_request_list_withdraw_retry_discard.button.withdraw_request"
              onClick={onClickWithdrawRequest}
            />
          </>
        )}
        {/* retry */}

        {pageMode ===
          DeploymentRequestScreenProcessingDivisionType.RETRY_MODE && (
          <>
            <FrVrfButton
              content="Back Component Deployment Request List"
              theme="secondary"
              size="large"
              onClick={onClickBackButton}
            />
            <FrVrfButton
              content="Retry Request"
              theme="primary"
              size="large"
              authActId="component_deployment_request_list_retry.button.retry_request"
              onClick={onClickRetryRequest}
            />
          </>
        )}

        {/* revert */}
        {pageMode ===
          DeploymentRequestScreenProcessingDivisionType.REVERT_MODE && (
          <>
            <FrVrfButton
              content="Back Component Deployment Request List"
              theme="secondary"
              size="large"
              onClick={onClickBackButton}
            />
            <FrVrfButton
              content="Revert Request"
              theme="primary"
              size="large"
              authActId="component_deployment_request_list_revert.button.revert_request"
              onClick={onClickRevertRequest}
            />
          </>
        )}
      </FrVrfFooter>
      {deploymentRequestInfomationInputDialogState.isShow && (
        <DeploymentRequestInfomationInputDialogContent />
      )}
    </>
  );
};
